import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatInputField } from "@cat-digital-blocks/react/bedrock/components/InputField";
import { CatTextareaField } from "@cat-digital-blocks/react/bedrock/components/TextareaField";
import { CatDropdown } from "@cat-digital-blocks/react/bedrock/components/Dropdown";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatList } from "@cat-digital-blocks/react/bedrock/components/List";
import { CatListItem } from "@cat-digital-blocks/react/bedrock/components/ListItem";
import { CatToggle } from "@cat-digital-blocks/react/bedrock/components/Toggle";
import { CatCheckboxField } from "@cat-digital-blocks/react/bedrock/components/CheckboxField";
import {
  useGetAttributeMappingsQuery,
  useUpdateRuleMappingMutation,
  useGetDealersQuery,
} from "@services/dcmApi";
import type { RuleMappingResponse } from "@/types/api";
import styles from "./RuleRegistration.module.scss";

interface EditRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  rule: RuleMappingResponse;
  userName?: string;
}

const EditRuleModal: React.FC<EditRuleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  rule,
  userName,
}) => {
  const { t } = useTranslation();

  // Form state - initialized from rule
  const [ruleDescription, setRuleDescription] = useState(
    rule.ruleDescription || "",
  );
  const [defaultValue, setDefaultValue] = useState(rule.defaultValue || "");
  const [ruleOperator, setRuleOperator] = useState(rule.ruleOperator || "");
  const [enableApplicableDealers, setEnableApplicableDealers] = useState(
    (rule.applicableDealers?.length ?? 0) > 0,
  );
  const [selectedDealers, setSelectedDealers] = useState<string[]>(
    rule.applicableDealers || [],
  );
  const [validFrom, setValidFrom] = useState(rule.validFrom || "");
  const [validUntil, setValidUntil] = useState(rule.validUntil || "");

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch attributes to get the allowed operators for this attribute
  const { data: attributes = [] } = useGetAttributeMappingsQuery({
    palType: rule.palType,
  });

  // Fetch dealers for multi-select
  const { data: dealers = [] } = useGetDealersQuery();

  const [updateRule] = useUpdateRuleMappingMutation();

  // Get the attribute associated with this rule
  const attribute = useMemo(() => {
    return attributes.find((attr) => attr.id === rule.attributeId);
  }, [attributes, rule.attributeId]);

  // Available operators from the attribute
  const availableOperators = useMemo(() => {
    return attribute?.allowedOperators || [];
  }, [attribute]);

  // Operator dropdown options
  const operatorOptions = useMemo(() => {
    return availableOperators.map((op) => ({
      label: op,
      value: op,
    }));
  }, [availableOperators]);

  // Reset form when rule changes
  useEffect(() => {
    setRuleDescription(rule.ruleDescription || "");
    setDefaultValue(rule.defaultValue || "");
    setRuleOperator(rule.ruleOperator || "");
    setEnableApplicableDealers((rule.applicableDealers?.length ?? 0) > 0);
    setSelectedDealers(rule.applicableDealers || []);
    setValidFrom(rule.validFrom || "");
    setValidUntil(rule.validUntil || "");
    setError(null);
  }, [rule]);

  const handleRuleDescriptionChange = useCallback((event: CustomEvent) => {
    setRuleDescription(event.detail?.value || "");
  }, []);

  const handleDefaultValueChange = useCallback((event: CustomEvent) => {
    setDefaultValue(event.detail?.value || "");
  }, []);

  const handleOperatorChange = useCallback((event: CustomEvent) => {
    setRuleOperator(event.detail?.value || "");
  }, []);

  const handleToggleApplicableDealers = useCallback((event: CustomEvent) => {
    const checked = event.detail?.checked ?? false;
    setEnableApplicableDealers(checked);
    if (!checked) {
      setSelectedDealers([]);
    }
  }, []);

  const handleDealerSelection = useCallback((event: CustomEvent) => {
    const selectedItems = event.detail?.values;
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      const newValues = selectedItems.map((item: string | { value: string }) =>
        typeof item === "string" ? item : item.value,
      );
      setSelectedDealers(newValues);
    } else {
      setSelectedDealers([]);
    }
  }, []);

  const handleValidFromChange = useCallback((event: CustomEvent) => {
    setValidFrom(event.detail?.value || "");
  }, []);

  const handleValidUntilChange = useCallback((event: CustomEvent) => {
    setValidUntil(event.detail?.value || "");
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    // Validation
    if (!ruleOperator) {
      setError(t("admin.ruleRegistration.validation.operatorRequired"));
      return;
    }

    if (!validFrom) {
      setError(t("admin.ruleRegistration.validation.validFromRequired"));
      return;
    }

    if (!validUntil) {
      setError(t("admin.ruleRegistration.validation.validUntilRequired"));
      return;
    }

    // Default rule language to en-US
    const ruleLanguage = "en-US";

    // Use selected dealers array if enabled
    const dealersArray = enableApplicableDealers && selectedDealers.length > 0
      ? selectedDealers
      : undefined;

    setIsLoading(true);

    try {
      await updateRule({
        ruleId: rule.id,
        body: {
          attributeId: rule.attributeId,
          ruleLanguage,
          ruleDescription: ruleDescription || undefined,
          defaultValue: defaultValue || undefined,
          ruleOperator,
          applicableDealers: dealersArray,
          validFrom,
          validUntil,
        },
        userName,
      }).unwrap();

      onSuccess();
    } catch (err: unknown) {
      if (
        typeof err === "object" &&
        err !== null &&
        "status" in err &&
        (err as { status: number }).status === 409
      ) {
        setError(t("admin.ruleRegistration.error.duplicate"));
      } else {
        setError(t("admin.ruleRegistration.error.updateFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    rule,
    ruleDescription,
    defaultValue,
    ruleOperator,
    enableApplicableDealers,
    selectedDealers,
    validFrom,
    validUntil,
    updateRule,
    userName,
    onSuccess,
    t,
  ]);

  const handleCancel = useCallback(() => {
    setRuleDescription(rule.ruleDescription || "");
    setDefaultValue(rule.defaultValue || "");
    setRuleOperator(rule.ruleOperator || "");
    setEnableApplicableDealers((rule.applicableDealers?.length ?? 0) > 0);
    setSelectedDealers(rule.applicableDealers || []);
    setValidFrom(rule.validFrom || "");
    setValidUntil(rule.validUntil || "");
    setError(null);
    onClose();
  }, [rule, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">
            {t("admin.ruleRegistration.editModal.title")}
          </CatHeading>
        </div>

        <div className={styles.modalBody}>
          {error && (
            <CatInlineNotification
              variant="error"
              className={styles.errorMessage}
            >
              {error}
            </CatInlineNotification>
          )}

          {/* Data Source (read-only) */}
          <div className={styles.formGroup}>
            <label>{t("admin.ruleRegistration.createModal.dataSource")}</label>
            <CatInputField label="" value={rule.sourceName} disabled={true} />
          </div>

          {/* Attribute (read-only) */}
          <div className={styles.formGroup}>
            <label>{t("admin.ruleRegistration.editModal.attribute")}</label>
            <CatInputField
              label=""
              value={rule.attributeName}
              disabled={true}
            />
          </div>

          {/* Rule Description */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.ruleDescription")}
            </label>
            <div className={styles.noLabelField}>
              <CatTextareaField
                label=" "
                value={ruleDescription}
                onBlChange={handleRuleDescriptionChange}
                placeholder={t(
                  "admin.ruleRegistration.createModal.ruleDescriptionPlaceholder",
                )}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Default Value */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.defaultValue")}
            </label>
            <CatInputField
              label=""
              value={defaultValue}
              onBlChange={handleDefaultValueChange}
              placeholder={t(
                "admin.ruleRegistration.createModal.defaultValuePlaceholder",
              )}
              disabled={isLoading}
            />
          </div>

          {/* Rule Operator */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.ruleOperator")} *
            </label>
            <CatDropdown
              label=""
              size="sm"
              placeholder={t(
                "admin.ruleRegistration.createModal.operatorPlaceholder",
              )}
              value={ruleOperator}
              onBlSelect={handleOperatorChange}
              disabled={isLoading}
            >
              <CatList spacing="padded">
                {operatorOptions.map((opt) => (
                  <CatListItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          </div>

          {/* Applicable Dealers Toggle and Multi-Select */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.applicableDealers")}
            </label>
            <div className={styles.dealerToggleRow}>
              <CatToggle
                label=""
                hideLabel
                checked={enableApplicableDealers}
                onBlToggleChange={handleToggleApplicableDealers}
                disabled={isLoading}
              >
                <span slot="checked-label">{t("common.enabled")}</span>
                <span slot="unchecked-label">{t("common.disabled")}</span>
              </CatToggle>
            </div>
            {enableApplicableDealers && (
              <div className={styles.dealerDropdown}>
                <CatDropdown
                  label=""
                  size="sm"
                  placeholder={t(
                    "admin.ruleRegistration.createModal.selectDealersPlaceholder",
                  )}
                  isMultiSelect={true}
                  disabled={isLoading}
                  onBlDropdownMultiSelect={handleDealerSelection}
                >
                  {dealers.map((dealer) => (
                    <CatCheckboxField
                      key={dealer.dealerCode}
                      value={dealer.dealerCode}
                      checked={selectedDealers.includes(dealer.dealerCode)}
                      hasHover
                    >
                      {dealer.displayName}
                    </CatCheckboxField>
                  ))}
                </CatDropdown>
              </div>
            )}
          </div>

          {/* Valid From & Valid Until */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>
                {t("admin.ruleRegistration.createModal.validFrom")} *
              </label>
              <CatInputField
                label=""
                value={validFrom}
                onBlChange={handleValidFromChange}
                placeholder="YYYY-MM-DD"
                disabled={isLoading}
              />
            </div>
            <div className={styles.formGroup}>
              <label>
                {t("admin.ruleRegistration.createModal.validUntil")} *
              </label>
              <CatInputField
                label=""
                value={validUntil}
                onBlChange={handleValidUntilChange}
                placeholder="YYYY-MM-DD"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <CatButton
            variant="contrast"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </CatButton>
          <CatButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {t("common.save")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

export default EditRuleModal;
